import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEnvelope, FaLock, FaGoogle, FaFacebook } from 'react-icons/fa';

const LoginForm = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const response = await axios.post('http://192.168.56.1:5001/api/users/login', formData);

      if (response.data.token) {
        // Store token and user data
        localStorage.setItem('token', response.data.token);
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        // Set authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        setSuccessMessage('Login successful!');
        navigate('/');
      } else {
        setError('Invalid login response from server');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledLoginPage>
      <motion.div 
        className="form-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="form-header">
          <motion.h2 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Welcome Back
          </motion.h2>
          <div className="header-accent"></div>
        </div>
        {successMessage && <div className="success-message">{successMessage}</div>}
        <form onSubmit={handleSubmit}>
          <motion.div 
            className="input-group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="icon"><FaEnvelope /></div>
            <input 
              type="email" 
              placeholder="Email address"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </motion.div>

          <motion.div 
            className="input-group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="icon"><FaLock /></div>
            <input 
              type="password" 
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
            />
          </motion.div>

          <motion.div 
            className="form-actions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <label className="remember-me">
              <input 
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
              />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="forgot-password">
              Forgot password?
            </Link>
          </motion.div>

          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
           {isSubmitting ? 'Logging in...' : 'Log In'}
          </motion.button>

          <div className="divider">
            <span>or continue with</span>
          </div>

          <div className="social-login">
            <motion.button
              type="button"
              className="google"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaGoogle /> Google
            </motion.button>
            <motion.button
              type="button"
              className="facebook"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaFacebook /> Facebook
            </motion.button>
          </div>

          <motion.div 
            className="signup-prompt"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Don't have an account? <Link to="/signup">Sign up</Link>
          </motion.div>
        </form>
      </motion.div>
      
      <div className="background-decoration"></div>
    </StyledLoginPage>
  );
};

const StyledLoginPage = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%);
  position: relative;
  overflow: hidden;

  .background-decoration {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 10% 20%, rgba(45, 123, 168, 0.05) 0%, transparent 20%),
      radial-gradient(circle at 90% 80%, rgba(52, 73, 94, 0.05) 0%, transparent 20%);
    z-index: 0;
  }

  .form-container {
    width: 100%;
    max-width: 480px;
    background: white;
    padding: 40px;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
    position: relative;
    z-index: 1;
  }

  .form-header {
    text-align: center;
    margin-bottom: 40px;

    h2 {
      font-size: 2rem;
      color: #1e293b;
      margin-bottom: 15px;
    }

    .header-accent {
      width: 60px;
      height: 4px;
      background: linear-gradient(45deg, #2d7ba8, #34495e);
      margin: 0 auto;
      border-radius: 2px;
    }
  }

  .input-group {
    position: relative;
    margin-bottom: 20px;

    .icon {
      position: absolute;
      left: 15px;
      top: 50%;
      transform: translateY(-50%);
      color: #94a3b8;
    }

    input {
      width: 100%;
      padding: 15px 15px 15px 45px;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      font-size: 1rem;
      transition: all 0.3s ease;

      &:focus {
        outline: none;
        border-color: #2d7ba8;
        box-shadow: 0 0 0 3px rgba(45, 123, 168, 0.1);
      }
    }
  }

  .form-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .remember-me {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #64748b;
      cursor: pointer;

      input {
        accent-color: #2d7ba8;
      }
    }

    .forgot-password {
      color: #2d7ba8;
      text-decoration: none;
      font-size: 0.9rem;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  button {
    width: 100%;
    padding: 15px;
    background: linear-gradient(45deg, #2d7ba8, #34495e);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-1px);
    }
  }

  .divider {
    text-align: center;
    margin: 20px 0;
    position: relative;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      width: calc(50% - 70px);
      height: 1px;
      background: #e2e8f0;
    }

    &::before {
      left: 0;
    }

    &::after {
      right: 0;
    }

    span {
      background: white;
      padding: 0 20px;
      color: #64748b;
      font-size: 0.9rem;
    }
  }

  .social-login {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 20px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      background: #f8fafc;
      color: #475569;
      border: 1px solid #e2e8f0;

      &.google:hover {
        background: #f1f5f9;
      }

      &.facebook {
        background: #1877f2;
        color: white;
        border: none;
      }
    }
  }

  .signup-prompt {
    text-align: center;
    color: #64748b;

    a {
      color: #2d7ba8;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media (max-width: 480px) {
    .form-container {
      padding: 30px 20px;
    }

    .social-login {
      grid-template-columns: 1fr;
    }
  }
     .success-message {
    color: green; // or whatever color you want for the success message
    margin-bottom: 20px;
    text-align: center;
  }
`;

export default LoginForm;